import { GameType } from './GameType.ts';
import { getNumDaysSinceEpoch } from '../utils/utils.js';
import { WEAVER__X_START_DAY_SINCE_EPOCH } from '../utils/wordPairsX.ts';
import { WEAVER_START_DAY_SINCE_EPOCH } from '../utils/wordPairs4.ts';
import { WordPair, WeaverGameData } from '../utils/wordPairUtils';
import { SUBTEXT_START_DAY_SINCE_EPOCH, SubtextGameData, SubtextSingleGameData } from '../utils/subtextGameData.ts';
import { DictionaryConfig, TWL06_BASE_DICTIONARY_CONFIG } from '../utils/dictionary.ts';
import { IconType } from '../utils/iconUtils.ts';

// Load game data once at module initialization
const weaverGameData = require('../utils/wordPairs4.ts').default as WeaverGameData;
const weaverXGameData = require('../utils/wordPairsX.ts').default as WeaverGameData;
const subtextGameData = require('../utils/subtextGameData.ts').default as SubtextGameData;

// Base interface for game data that supports dictionaries
export interface GameDataWithDictionary {
  dictionary?: {
    type: string;
    version: number;
    patch: number;
  };
}

export interface BaseGameConfig<T = any> {
  type: GameType;
  routePath: string;
  name: string;
  shortDescription: string;
  icon: IconType;
  primaryColor: string;
  startDaySinceEpoch: number;
  requiresDictionary: boolean;
  defaultDictionaryConfig?: DictionaryConfig;

  // Static methods that work with startDaySinceEpoch
  hasGameLaunched(): boolean;
  getCurrentGameNumber(): number;
  isGameNumberCurrentGame(gameNumber: number): boolean;
  isValidGameNumber(gameNumber: number): boolean;
  getEpochDayFromGameNumber(gameNumber: number): number;

  // Synchronous data loading methods
  getGameData(gameNumber: number): T | null;
}

export interface WeaverGameConfig extends BaseGameConfig<WordPair> {
  // Weaver-specific methods
  getSpecialEmoji(gameNumber: number): string | null;
  getSpecialMessageData(gameNumber: number): [string | null, string | null];
  getYesterdayOptimalPath(): string[];
}

export interface SubtextGameConfig extends BaseGameConfig<SubtextSingleGameData> {
  getGameData(gameNumber: number): SubtextSingleGameData | null;
}

export type GameConfig = WeaverGameConfig | SubtextGameConfig;

// Helper function to create game methods based on startDaySinceEpoch
function createGameMethods(startDaySinceEpoch: number) {
  return {
    hasGameLaunched: () => getNumDaysSinceEpoch() >= startDaySinceEpoch,
    getCurrentGameNumber: () => (getNumDaysSinceEpoch() - startDaySinceEpoch) + 1,
    isGameNumberCurrentGame: (gameNumber: number) => gameNumber === ((getNumDaysSinceEpoch() - startDaySinceEpoch) + 1),
    isValidGameNumber: (gameNumber: number) => gameNumber >= 1 && gameNumber <= ((getNumDaysSinceEpoch() - startDaySinceEpoch) + 1),
    getEpochDayFromGameNumber: (gameNumber: number) => startDaySinceEpoch + gameNumber - 1,
  };
}

const gameConfigs: Record<GameType, GameConfig> = {
  [GameType.WEAVER]: {
    type: GameType.WEAVER,
    routePath: '/weaver/',
    name: 'Weaver',
    shortDescription: 'Classic word ladder game',
    icon: IconType.WEAVER,
    primaryColor: '#2E7D32',
    startDaySinceEpoch: WEAVER_START_DAY_SINCE_EPOCH,
    requiresDictionary: true,
    defaultDictionaryConfig: TWL06_BASE_DICTIONARY_CONFIG,
    ...createGameMethods(WEAVER_START_DAY_SINCE_EPOCH),
    getGameData(gameNumber: number): WordPair | null {
      return weaverGameData.getDailyGame(gameNumber);
    },
    getSpecialEmoji(gameNumber: number): string | null {
      return weaverGameData.getSpecialEmoji(gameNumber);
    },
    getSpecialMessageData(gameNumber: number): [string | null, string | null] {
      return weaverGameData.getSpecialMessageData(gameNumber);
    },
    getYesterdayOptimalPath(): string[] {
      const currentGameNumber = this.getCurrentGameNumber();
      const yesterdayGameNumber = currentGameNumber - 1;

      if (yesterdayGameNumber < 1) {
        return []; // No yesterday game if we're on game 1
      }

      const yesterdayWordPair = weaverGameData.getDailyGame(yesterdayGameNumber);
      return yesterdayWordPair?.optimalPath ?? [];
    },
  },
  [GameType.WEAVER_X]: {
    type: GameType.WEAVER_X,
    routePath: '/weaver-x/',
    name: 'Weaver X',
    shortDescription: 'Word ladder game, with a twist',
    icon: IconType.WEAVER_X,
    primaryColor: '#1565C0',
    startDaySinceEpoch: WEAVER__X_START_DAY_SINCE_EPOCH,
    requiresDictionary: true,
    defaultDictionaryConfig: TWL06_BASE_DICTIONARY_CONFIG,
    ...createGameMethods(WEAVER__X_START_DAY_SINCE_EPOCH),
    getGameData(gameNumber: number): WordPair | null {
      return weaverXGameData.getDailyGame(gameNumber);
    },
    getSpecialEmoji(gameNumber: number): string | null {
      return weaverXGameData.getSpecialEmoji(gameNumber);
    },
    getSpecialMessageData(gameNumber: number): [string | null, string | null] {
      return weaverXGameData.getSpecialMessageData(gameNumber);
    },
    getYesterdayOptimalPath(): string[] {
      const currentGameNumber = this.getCurrentGameNumber();
      const yesterdayGameNumber = currentGameNumber - 1;

      if (yesterdayGameNumber < 1) {
        return []; // No yesterday game if we're on game 1
      }

      const yesterdayWordPair = weaverXGameData.getDailyGame(yesterdayGameNumber);
      return yesterdayWordPair?.optimalPath ?? [];
    },
  },
  [GameType.SUBTEXT]: {
    type: GameType.SUBTEXT,
    routePath: '/subtext/',
    name: 'Subtext',
    shortDescription: 'Find all the subwords',
    icon: IconType.SUBTEXT,
    primaryColor: '#6A1B9A',
    startDaySinceEpoch: SUBTEXT_START_DAY_SINCE_EPOCH,
    requiresDictionary: false,
    ...createGameMethods(SUBTEXT_START_DAY_SINCE_EPOCH),
    getGameData(gameNumber: number): SubtextSingleGameData | null {
      return subtextGameData.getDailyGame(gameNumber);
    },
  }
};

export function getGameConfig(gameType: GameType): GameConfig {
  return gameConfigs[gameType];
} 